/**
 * Platform-neutral analytics capabilities. NO posthog import here — mobile
 * must be able to depend on these tags without dragging `posthog-js` into the
 * RN bundle. The web layers live in `./client-effect`; a mobile layer over
 * `posthog-react-native` is planned — see
 * docs/projects/active/auth-session-persistence-and-logout-telemetry.md.
 *
 * Two tags, not one: capture/identify/reset exist on both platforms, while the
 * flag surface (edge bootstrap, local overrides) is web-only until the mobile
 * SDK layer lands — separate tags let a runtime provide one without stubbing
 * the other.
 */
import { Context, type Effect } from 'effect';
import type { ClientEvents } from './events/client-events';
import type { ClientFlagName, ClientFlags } from './flags/client-flags';
import type { FlagVariant } from './flags/flag-types';

export type AnalyticsService = {
  /** Identify the current user. Triggers an async flag refetch for the new user. */
  identify: (
    userId: string,
    traits?: Record<string, unknown>,
  ) => Effect.Effect<void>;
  /** Reset identity and flags. Call on logout. */
  reset: Effect.Effect<void>;
  /** Track a typed analytics event. */
  capture: <E extends keyof ClientEvents>(
    event: E,
    properties: ClientEvents[E],
  ) => Effect.Effect<void>;
};

export class Analytics extends Context.Service<Analytics, AnalyticsService>()(
  '@listening/frontend/analytics/Analytics',
) {}

export type FeatureFlagsService = {
  /** Read a flag value synchronously. Returns the typed variant or default. */
  getFlag: <F extends ClientFlagName>(
    flag: F,
  ) => Effect.Effect<FlagVariant<(typeof ClientFlags)[F]>>;
  /** Check if flags have been loaded (bootstrap or async fetch). */
  hasLoadedFlags: Effect.Effect<boolean>;
};

export class FeatureFlags extends Context.Service<
  FeatureFlags,
  FeatureFlagsService
>()('@listening/frontend/analytics/FeatureFlags') {}
